<template>
  <div class="settings-page">
    <h1>Settings</h1>

    <!-- Monarch Money Section -->
    <div class="settings-section">
      <h2>Monarch Money</h2>
      <p class="section-description">
        Connect your Monarch Money account to sync categories, transactions, and budget data.
      </p>

      <div v-if="loading" class="status-loading">Checking connection status...</div>

      <div v-else-if="isConnected && needsReauth" class="connection-card needs-reauth">
        <div class="connection-info">
          <span class="status-badge badge-reauth">Needs Re-authentication</span>
        </div>
        <p class="reauth-message">Syncing paused — your Monarch session has expired.</p>
        <div class="connection-actions">
          <MonarchConnect @connected="handleReconnected" />
        </div>
      </div>

      <div v-else-if="isConnected" class="connection-card connected">
        <div class="connection-info">
          <span class="status-badge badge-connected">Connected</span>
          <span v-if="connectedAt" class="connected-date">
            since {{ formatDate(connectedAt) }}
          </span>
        </div>
        <div class="connection-actions">
          <button
            class="btn-disconnect"
            :disabled="disconnecting"
            @click="handleDisconnect"
          >
            {{ disconnecting ? 'Disconnecting...' : 'Disconnect' }}
          </button>
        </div>
      </div>

      <div v-else class="connection-card disconnected">
        <div class="connection-info">
          <span class="status-badge badge-disconnected">Not Connected</span>
        </div>
        <div class="connection-actions">
          <MonarchConnect @connected="checkStatus" />
        </div>
      </div>

      <div v-if="error" class="error-alert">{{ error }}</div>
    </div>

    <!-- Email Forwarding Section -->
    <div class="settings-section" style="margin-top: 1.5rem;">
      <h2>Email Forwarding</h2>
      <p class="section-description">
        Forward retailer receipt emails to Budget Buddy for automatic processing.
      </p>

      <div v-if="forwardingLoading" class="status-loading">Checking forwarding status...</div>

      <div v-else-if="forwardingVerified" class="connection-card connected">
        <div class="connection-info">
          <span class="status-badge badge-connected">Verified</span>
          <span class="connected-date">{{ primaryForwardingEmail }}</span>
        </div>
        <div class="connection-actions">
          <router-link to="/forwarding/setup" class="btn-manage">
            Manage
          </router-link>
        </div>
      </div>

      <!-- Pending Confirmation State -->
      <div v-else-if="hasPendingConfirmation" class="connection-card pending">
        <div class="connection-info">
          <span class="status-badge badge-pending">Awaiting Verification</span>
          <span class="connected-date">Confirmation email received</span>
        </div>
        <div class="pending-instructions">
          <p>We received Gmail's forwarding confirmation. To complete setup:</p>
          <ol>
            <li>Click the verification link below</li>
            <li>Come back here and click "I've Verified"</li>
          </ol>
        </div>

        <!-- Verification URL display -->
        <div v-if="verificationUrl" class="verification-link-box">
          <a :href="verificationUrl" target="_blank" rel="noopener" class="verification-link">
            Click here to verify forwarding in Gmail
          </a>
        </div>

        <div class="pending-action-buttons">
          <button @click="showConfirmationEmail" class="btn-view-confirmation">
            View Full Email
          </button>
          <button
            @click="confirmForwardingVerified"
            :disabled="confirmingForwarding"
            class="btn-confirm-verified"
          >
            {{ confirmingForwarding ? 'Confirming...' : "I've Verified" }}
          </button>
        </div>
        <div v-if="forwardingError" class="error-alert" style="margin-top: 0.75rem;">
          {{ forwardingError }}
        </div>
      </div>

      <!-- Waiting for Confirmation Email -->
      <div v-else-if="awaitingConfirmationEmail" class="connection-card waiting">
        <div class="connection-info">
          <span class="status-badge badge-waiting">Waiting for Email</span>
          <span class="connected-date">Waiting for Gmail confirmation...</span>
        </div>
        <div class="pending-instructions">
          <p>Complete the Gmail forwarding setup, then we'll receive the confirmation email automatically.</p>
          <div class="waiting-indicator">
            <span class="spinner"></span>
            <span>Checking for confirmation email...</span>
          </div>
        </div>
        <div class="pending-action-buttons">
          <router-link to="/forwarding/setup" class="btn-manage">
            View Setup Instructions
          </router-link>
        </div>
      </div>

      <div v-else class="connection-card disconnected">
        <div class="connection-info">
          <span class="status-badge badge-disconnected">Not Set Up</span>
        </div>
        <div class="connection-actions">
          <router-link to="/forwarding/setup" class="btn-setup">
            Set Up Forwarding
          </router-link>
        </div>
      </div>
    </div>

    <!-- Forwarding Confirmation Email Modal -->
    <div v-if="showForwardingEmailModal" class="modal-overlay" @click.self="showForwardingEmailModal = false">
      <div class="confirmation-email-modal">
        <div class="modal-header">
          <h3>Gmail Forwarding Confirmation</h3>
          <button @click="showForwardingEmailModal = false" class="btn-close">&times;</button>
        </div>

        <!-- Verification URL at top of modal -->
        <div v-if="verificationUrl" class="modal-verification-link">
          <a :href="verificationUrl" target="_blank" rel="noopener" class="verification-link-btn">
            Click here to verify forwarding
          </a>
        </div>

        <p class="modal-instructions">
          After clicking the verification link above, close this and click "I've Verified".
        </p>

        <div class="email-content-wrapper">
          <div class="email-content-label">Full Email Content:</div>
          <div v-if="confirmationEmailHtml" class="email-content" v-html="confirmationEmailHtml"></div>
          <div v-else class="email-loading">Loading confirmation email...</div>
        </div>

        <div class="modal-footer">
          <button @click="showForwardingEmailModal = false" class="btn-secondary">
            Close
          </button>
          <button
            @click="confirmFromModal"
            :disabled="confirmingForwarding"
            class="btn-confirm-verified"
          >
            {{ confirmingForwarding ? 'Confirming...' : "I've Verified" }}
          </button>
        </div>
      </div>
    </div>

    <!-- Household Section -->
    <div class="settings-section" style="margin-top: 1.5rem;">
      <h2>Household</h2>
      <p class="section-description">
        Share receipts with family members who use the same Monarch account.
      </p>

      <div v-if="householdLoading" class="status-loading">Loading household...</div>

      <div v-else-if="household" class="household-card">
        <div class="household-header">
          <div class="household-info">
            <span class="household-name">{{ household.name }}</span>
            <span class="member-count">{{ householdMembers.length }} member(s)</span>
          </div>
          <div class="household-actions">
            <button v-if="isHouseholdOwner" @click="showInviteModal = true" class="btn-invite">
              Invite Member
            </button>
            <button v-if="!isHouseholdOwner" @click="showLeaveConfirm = true" class="btn-leave">
              Leave
            </button>
          </div>
        </div>

        <!-- Member list -->
        <div class="members-list">
          <div v-for="member in householdMembers" :key="member.id" class="member-row">
            <div class="member-info">
              <img v-if="member.picture" :src="member.picture" class="member-avatar" alt="" />
              <div v-else class="member-avatar-placeholder">{{ member.name?.charAt(0) || '?' }}</div>
              <div class="member-details">
                <span class="member-name">{{ member.name || member.email }}</span>
                <span v-if="member.is_owner" class="owner-badge">Owner</span>
              </div>
            </div>
            <div class="member-actions">
              <span v-if="member.share_receipts" class="sharing-badge">Sharing</span>
              <span v-else class="not-sharing-badge">Private</span>
              <button
                v-if="isHouseholdOwner && !member.is_owner"
                @click="removeMember(member.id)"
                class="btn-remove-member"
                title="Remove from household"
              >
                &times;
              </button>
            </div>
          </div>
        </div>

        <!-- Sent Pending Invites (for owner) -->
        <div v-if="isHouseholdOwner && sentInvites.length > 0" class="sent-invites-section">
          <h4>Pending Sent Invites</h4>
          <div v-for="invite in sentInvites" :key="invite.token" class="sent-invite-row">
            <div class="sent-invite-info">
              <span class="sent-invite-email">{{ invite.invited_email || 'Link invite' }}</span>
              <span class="sent-invite-expires">Expires {{ formatInviteExpiry(invite.expires_at) }}</span>
            </div>
            <button
              @click="revokeInvite(invite.token)"
              :disabled="revokingInvite === invite.token"
              class="btn-revoke-invite"
              title="Revoke invite"
            >
              {{ revokingInvite === invite.token ? '...' : 'Revoke' }}
            </button>
          </div>
        </div>

        <!-- Sharing preference toggle -->
        <label class="toggle-row" style="margin-top: 1rem;">
          <input
            type="checkbox"
            v-model="shareReceipts"
            :disabled="savingShare"
            @change="saveShareReceipts"
          />
          <div class="toggle-content">
            <span class="toggle-label">Share my receipts with household</span>
            <span class="toggle-hint">Allow other household members to see your itemized transactions</span>
          </div>
        </label>
      </div>

      <div v-if="householdError" class="error-alert" style="margin-top: 0.75rem;">{{ householdError }}</div>

      <!-- Pending Invites Section -->
      <div v-if="pendingInvites.length > 0" class="pending-invites-section">
        <h3>Pending Invitations</h3>
        <p class="pending-invites-hint">You've been invited to join the following households:</p>

        <div v-for="invite in pendingInvites" :key="invite.id" class="pending-invite-card">
          <div class="pending-invite-info">
            <span class="pending-household-name">{{ invite.household_name }}</span>
            <span class="pending-invite-from">Invited by {{ invite.owner_name }}</span>
          </div>
          <div class="pending-invite-actions">
            <button
              @click="acceptInvite(invite.id)"
              :disabled="respondingToInvite === invite.id"
              class="btn-accept-invite"
            >
              {{ respondingToInvite === invite.id ? '...' : 'Accept' }}
            </button>
            <button
              @click="declineInvite(invite.id)"
              :disabled="respondingToInvite === invite.id"
              class="btn-decline-invite"
            >
              Decline
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Invite Modal -->
    <div v-if="showInviteModal" class="modal-overlay" @click.self="showInviteModal = false">
      <div class="confirm-modal invite-modal">
        <h3>Add Family Member</h3>

        <!-- Invite by Email Section -->
        <div class="invite-section">
          <h4>Invite by Email</h4>
          <p>Send an invite to someone who already has a Budget Buddy account.</p>

          <div v-if="addByEmailSuccess" class="success-message">
            {{ addByEmailSuccess }}
          </div>

          <div class="email-add-form">
            <input
              type="email"
              v-model="addByEmail"
              placeholder="Enter their email address"
              class="email-input"
              @keyup.enter="inviteMemberByEmail"
            />
            <button
              @click="inviteMemberByEmail"
              :disabled="addingByEmail || !addByEmail.trim()"
              class="btn-add-email"
            >
              {{ addingByEmail ? 'Sending...' : 'Send Invite' }}
            </button>
          </div>
        </div>

        <div class="section-divider">
          <span>or</span>
        </div>

        <!-- Invite Link Section -->
        <div class="invite-section">
          <h4>Share Invite Link</h4>
          <p>Share a link with someone who doesn't have an account yet.</p>

          <div v-if="inviteLink" class="invite-link-box">
            <input type="text" :value="inviteLink" readonly class="invite-link-input" />
            <button @click="copyInviteLink" class="btn-copy">
              {{ copied ? 'Copied!' : 'Copy' }}
            </button>
          </div>

          <div v-else class="invite-actions">
            <button @click="generateInviteLink" :disabled="generatingInvite" class="btn-generate">
              {{ generatingInvite ? 'Generating...' : 'Generate Invite Link' }}
            </button>
          </div>
        </div>

        <div v-if="householdError" class="error-alert" style="margin-top: 0.75rem;">
          {{ householdError }}
        </div>

        <div class="confirm-actions" style="margin-top: 1rem;">
          <button class="btn-text" @click="closeInviteModal">Close</button>
        </div>
      </div>
    </div>

    <!-- Leave Household Confirmation -->
    <div v-if="showLeaveConfirm" class="modal-overlay" @click.self="showLeaveConfirm = false">
      <div class="confirm-modal">
        <h3>Leave Household?</h3>
        <p>You will no longer see shared receipts from other household members. Your receipts will become private.</p>
        <div class="confirm-actions">
          <button class="btn-text" @click="showLeaveConfirm = false">Cancel</button>
          <button class="btn-danger" @click="leaveHousehold">Leave</button>
        </div>
      </div>
    </div>

    <!-- Preferences Section -->
    <div class="settings-section" style="margin-top: 1.5rem;">
      <h2>Preferences</h2>
      <p class="section-description">
        Customize how Budget Buddy works for you.
      </p>

      <div class="preferences-list">
        <label class="toggle-row">
          <input
            type="checkbox"
            v-model="budgetedOnly"
            @change="saveBudgetedOnly"
          />
          <div class="toggle-content">
            <span class="toggle-label">Only show budgeted categories in item dropdowns</span>
            <span class="toggle-hint">Filter category dropdowns to show only budgeted items</span>
          </div>
        </label>

        <label class="toggle-row">
          <input
            type="checkbox"
            v-model="budgetedCategoriesAI"
            :disabled="savingPrefs"
            @change="saveBudgetedCategoriesAI"
          />
          <div class="toggle-content">
            <span class="toggle-label">Only suggest budgeted categories (AI)</span>
            <span class="toggle-hint">Restrict AI category suggestions to budgeted categories only</span>
          </div>
        </label>

        <label class="toggle-row">
          <input
            type="checkbox"
            v-model="autoUpdateTransactions"
            :disabled="savingPrefs"
            @change="saveAutoUpdate"
          />
          <div class="toggle-content">
            <span class="toggle-label">Auto-update transactions daily</span>
            <span class="toggle-hint">Automatically match receipts and sync categories to Monarch every 24 hours</span>
          </div>
        </label>
      </div>
      <div v-if="prefsError" class="error-alert" style="margin-top: 0.75rem;">{{ prefsError }}</div>
    </div>

    <!-- Dismissed Receipts Section -->
    <div class="settings-section" style="margin-top: 1.5rem;">
      <h2>Dismissed Receipts</h2>
      <p class="section-description">
        View email receipts that were dismissed (e.g. $0 orders covered by gift card or credit).
      </p>
      <router-link to="/receipts/dismissed" class="btn-view-receipts">
        View Dismissed Receipts
      </router-link>
    </div>

    <!-- Confirmation Modal -->
    <div v-if="showConfirm" class="modal-overlay" @click.self="showConfirm = false">
      <div class="confirm-modal">
        <h3>Disconnect Monarch?</h3>
        <p>This will remove your stored Monarch credentials and session. You can reconnect at any time.</p>
        <div class="confirm-actions">
          <button class="btn-text" @click="showConfirm = false">Cancel</button>
          <button class="btn-danger" @click="confirmDisconnect">Disconnect</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import DOMPurify from 'dompurify'
import { syncAPI, authAPI, forwardingAPI, householdAPI } from '../services/api'
import { useAuthStore } from '../store/auth'
import MonarchConnect from '../components/MonarchConnect.vue'

const authStore = useAuthStore()

const loading = ref(true)
const isConnected = ref(false)
const needsReauth = ref(false)
const connectedAt = ref(null)
const canManageMonarch = ref(false)
const disconnecting = ref(false)
const showConfirm = ref(false)
const error = ref(null)
const budgetedOnly = ref(localStorage.getItem('budgetedCategoriesOnly') === 'true')
const budgetedCategoriesAI = ref(false)
const autoUpdateTransactions = ref(false)
const savingPrefs = ref(false)
const prefsError = ref(null)

// Email forwarding state
const forwardingLoading = ref(true)
const forwardingVerified = ref(false)
const primaryForwardingEmail = ref('')
const hasPendingConfirmation = ref(false)
const awaitingConfirmationEmail = ref(false)
const showForwardingEmailModal = ref(false)
const confirmationEmailHtml = ref('')
const verificationUrl = ref('')
const confirmingForwarding = ref(false)
const forwardingError = ref(null)
let forwardingPollInterval = null

// Household state
const householdLoading = ref(true)
const household = ref(null)
const householdMembers = ref([])
const isHouseholdOwner = ref(false)
const householdError = ref(null)
const shareReceipts = ref(true)
const savingShare = ref(false)

// Invite modal state
const showInviteModal = ref(false)
const inviteLink = ref('')
const generatingInvite = ref(false)
const copied = ref(false)
const addByEmail = ref('')
const addingByEmail = ref(false)
const addByEmailSuccess = ref('')

// Leave modal state
const showLeaveConfirm = ref(false)

// Pending invites state (invites received by user)
const pendingInvites = ref([])
const pendingInvitesLoading = ref(false)
const respondingToInvite = ref(null)

// Sent invites state (invites sent by user as owner)
const sentInvites = ref([])
const sentInvitesLoading = ref(false)
const revokingInvite = ref(null)

const saveBudgetedOnly = () => {
  localStorage.setItem('budgetedCategoriesOnly', budgetedOnly.value ? 'true' : 'false')
}

const saveBudgetedCategoriesAI = async () => {
  savingPrefs.value = true
  prefsError.value = null
  try {
    await authAPI.updatePreferences({
      budgeted_categories_only: budgetedCategoriesAI.value
    })
  } catch (err) {
    prefsError.value = err.response?.data?.error || 'Failed to save preference'
    budgetedCategoriesAI.value = !budgetedCategoriesAI.value
  } finally {
    savingPrefs.value = false
  }
}

const saveAutoUpdate = async () => {
  savingPrefs.value = true
  prefsError.value = null
  try {
    await authAPI.updatePreferences({
      auto_update_transactions: autoUpdateTransactions.value
    })
  } catch (err) {
    prefsError.value = err.response?.data?.error || 'Failed to save preference'
    autoUpdateTransactions.value = !autoUpdateTransactions.value
  } finally {
    savingPrefs.value = false
  }
}

const loadPreferences = async () => {
  try {
    const response = await authAPI.getPreferences()
    budgetedCategoriesAI.value = response.data.budgeted_categories_only || false
    autoUpdateTransactions.value = response.data.auto_update_transactions || false
    shareReceipts.value = response.data.share_receipts_with_household !== false
  } catch (err) {
    console.error('Failed to load preferences:', err)
  }
}

const loadForwardingStatus = async (startPolling = true) => {
  forwardingLoading.value = true
  forwardingError.value = null
  try {
    const response = await forwardingAPI.getStatus()
    forwardingVerified.value = response.data.forwarding_verified || false
    primaryForwardingEmail.value = response.data.primary_email || ''
    hasPendingConfirmation.value = response.data.has_pending_confirmation || false

    // If we have pending confirmation, also load the verification URL
    if (hasPendingConfirmation.value && startPolling) {
      loadVerificationUrl()
      stopForwardingPolling() // Stop any existing polling
    }

    // Determine if we should show "waiting" state and start polling
    // This happens if user is in the middle of setup but hasn't received confirmation yet
    awaitingConfirmationEmail.value = false
  } catch (err) {
    console.error('Failed to load forwarding status:', err)
  } finally {
    forwardingLoading.value = false
  }
}

const loadVerificationUrl = async () => {
  try {
    const response = await forwardingAPI.getPendingConfirmation()
    if (response.data.has_confirmation) {
      verificationUrl.value = response.data.verification_url || ''
    }
  } catch (err) {
    console.error('Failed to load verification URL:', err)
  }
}

const startForwardingPolling = () => {
  // Poll for pending confirmation email
  forwardingPollInterval = setInterval(async () => {
    try {
      const response = await forwardingAPI.getStatus()
      if (response.data.has_pending_confirmation) {
        hasPendingConfirmation.value = true
        awaitingConfirmationEmail.value = false
        stopForwardingPolling()
        loadVerificationUrl()
      } else if (response.data.forwarding_verified) {
        forwardingVerified.value = true
        hasPendingConfirmation.value = false
        awaitingConfirmationEmail.value = false
        stopForwardingPolling()
      }
    } catch (err) {
      console.error('Error polling forwarding status:', err)
    }
  }, 5000)
}

const stopForwardingPolling = () => {
  if (forwardingPollInterval) {
    clearInterval(forwardingPollInterval)
    forwardingPollInterval = null
  }
}

const showConfirmationEmail = async () => {
  showForwardingEmailModal.value = true
  confirmationEmailHtml.value = ''

  try {
    const response = await forwardingAPI.getPendingConfirmation()
    if (response.data.has_confirmation) {
      // Sanitize HTML to prevent XSS attacks
      confirmationEmailHtml.value = DOMPurify.sanitize(response.data.email_html)
      verificationUrl.value = response.data.verification_url || ''
    }
  } catch (err) {
    console.error('Failed to load confirmation email:', err)
    confirmationEmailHtml.value = '<p style="color: #c0392b;">Failed to load confirmation email. Please try again.</p>'
  }
}

const confirmFromModal = async () => {
  await confirmForwardingVerified()
  if (forwardingVerified.value) {
    showForwardingEmailModal.value = false
  }
}

const confirmForwardingVerified = async () => {
  confirmingForwarding.value = true
  forwardingError.value = null

  try {
    await forwardingAPI.confirmVerified()
    // Reload status to update UI
    await loadForwardingStatus()
    showForwardingEmailModal.value = false
  } catch (err) {
    forwardingError.value = err.response?.data?.error || 'Failed to confirm verification. Make sure you clicked the link in the email.'
  } finally {
    confirmingForwarding.value = false
  }
}

const loadHousehold = async () => {
  householdLoading.value = true
  householdError.value = null
  try {
    const response = await householdAPI.get()
    household.value = response.data.household
    householdMembers.value = response.data.household?.members || []
    isHouseholdOwner.value = response.data.is_owner || false
  } catch (err) {
    if (err.response?.status !== 404) {
      householdError.value = 'Failed to load household'
    }
  } finally {
    householdLoading.value = false
  }
}

const saveShareReceipts = async () => {
  savingShare.value = true
  householdError.value = null
  try {
    await authAPI.updatePreferences({
      share_receipts_with_household: shareReceipts.value
    })
    // Reload household to update member list
    await loadHousehold()
  } catch (err) {
    householdError.value = err.response?.data?.error || 'Failed to save sharing preference'
    // Revert on error
    shareReceipts.value = !shareReceipts.value
  } finally {
    savingShare.value = false
  }
}

const generateInviteLink = async () => {
  generatingInvite.value = true
  try {
    const response = await householdAPI.createInvite({})
    inviteLink.value = response.data.invite_url
    // Refresh sent invites to show the new pending invite
    await loadSentInvites()
  } catch (err) {
    householdError.value = err.response?.data?.error || 'Failed to generate invite link'
  } finally {
    generatingInvite.value = false
  }
}

const copyInviteLink = async () => {
  try {
    await navigator.clipboard.writeText(inviteLink.value)
    copied.value = true
    setTimeout(() => { copied.value = false }, 2000)
  } catch (err) {
    console.error('Failed to copy:', err)
  }
}

const closeInviteModal = () => {
  showInviteModal.value = false
  inviteLink.value = ''
  copied.value = false
  addByEmail.value = ''
  addByEmailSuccess.value = ''
}

const inviteMemberByEmail = async () => {
  if (!addByEmail.value.trim()) return

  addingByEmail.value = true
  householdError.value = null
  addByEmailSuccess.value = ''

  try {
    const response = await householdAPI.inviteMemberByEmail(addByEmail.value.trim())
    addByEmailSuccess.value = response.data.message
    addByEmail.value = ''
    // Refresh sent invites to show the new pending invite
    await loadSentInvites()
  } catch (err) {
    householdError.value = err.response?.data?.error || 'Failed to send invite'
  } finally {
    addingByEmail.value = false
  }
}

const removeMember = async (userId) => {
  if (!confirm('Remove this member from your household?')) return

  try {
    await householdAPI.removeMember(userId)
    await loadHousehold()
  } catch (err) {
    householdError.value = err.response?.data?.error || 'Failed to remove member'
  }
}

const leaveHousehold = async () => {
  showLeaveConfirm.value = false
  try {
    await householdAPI.leave()
    // Reload household data
    await loadHousehold()
  } catch (err) {
    householdError.value = err.response?.data?.error || 'Failed to leave household'
  }
}

const loadPendingInvites = async () => {
  pendingInvitesLoading.value = true
  try {
    const response = await householdAPI.getPendingInvites()
    pendingInvites.value = response.data.invites || []
  } catch (err) {
    console.error('Failed to load pending invites:', err)
  } finally {
    pendingInvitesLoading.value = false
  }
}

const loadSentInvites = async () => {
  if (!isHouseholdOwner.value) return
  sentInvitesLoading.value = true
  try {
    const response = await householdAPI.listInvites()
    sentInvites.value = response.data.invites || []
  } catch (err) {
    console.error('Failed to load sent invites:', err)
  } finally {
    sentInvitesLoading.value = false
  }
}

const revokeInvite = async (token) => {
  if (!confirm('Revoke this invite? The recipient will no longer be able to use it.')) return
  revokingInvite.value = token
  try {
    await householdAPI.revokeInvite(token)
    await loadSentInvites()
  } catch (err) {
    householdError.value = err.response?.data?.error || 'Failed to revoke invite'
  } finally {
    revokingInvite.value = null
  }
}

const acceptInvite = async (inviteId) => {
  respondingToInvite.value = inviteId
  householdError.value = null
  try {
    await householdAPI.acceptPendingInvite(inviteId)
    // Reload everything
    await loadHousehold()
    await loadPendingInvites()
  } catch (err) {
    householdError.value = err.response?.data?.error || 'Failed to accept invite'
  } finally {
    respondingToInvite.value = null
  }
}

const declineInvite = async (inviteId) => {
  respondingToInvite.value = inviteId
  try {
    await householdAPI.declinePendingInvite(inviteId)
    await loadPendingInvites()
  } catch (err) {
    householdError.value = err.response?.data?.error || 'Failed to decline invite'
  } finally {
    respondingToInvite.value = null
  }
}

onMounted(async () => {
  checkStatus()
  loadPreferences()
  loadForwardingStatus()
  await loadHousehold()
  loadPendingInvites()
  loadSentInvites()
})

onUnmounted(() => {
  stopForwardingPolling()
})

const checkStatus = async () => {
  loading.value = true
  error.value = null
  try {
    const response = await syncAPI.getMonarchStatus()
    isConnected.value = response.data.connected
    connectedAt.value = response.data.connected_at
    canManageMonarch.value = response.data.can_manage || false
    needsReauth.value = response.data.needs_reauth || false
  } catch (err) {
    error.value = 'Failed to check Monarch connection status'
  } finally {
    loading.value = false
  }
}

const handleReconnected = async () => {
  needsReauth.value = false
  authStore.clearMonarchReauth()
  await checkStatus()
}

const handleDisconnect = () => {
  showConfirm.value = true
}

const confirmDisconnect = async () => {
  showConfirm.value = false
  disconnecting.value = true
  error.value = null

  try {
    await syncAPI.monarchDisconnect()
    isConnected.value = false
    connectedAt.value = null
  } catch (err) {
    error.value = err.response?.data?.error || 'Failed to disconnect Monarch account'
  } finally {
    disconnecting.value = false
  }
}

const formatDate = (isoString) => {
  if (!isoString) return ''
  const date = new Date(isoString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const formatInviteExpiry = (isoString) => {
  if (!isoString) return ''
  const date = new Date(isoString)
  const now = new Date()
  const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24))
  if (diffDays <= 0) return 'soon'
  if (diffDays === 1) return 'in 1 day'
  if (diffDays < 7) return `in ${diffDays} days`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
</script>

<style scoped>
.settings-page {
  max-width: 700px;
}

.settings-page h1 {
  color: #2c3e50;
  margin-bottom: 2rem;
}

.settings-section {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.settings-section h2 {
  color: #2c3e50;
  font-size: 1.25rem;
  margin-bottom: 0.5rem;
}

.section-description {
  color: #7f8c8d;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
}

.status-loading {
  color: #7f8c8d;
  padding: 1rem 0;
}

.connection-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid #eee;
}

.connection-card.connected {
  background-color: #f0faf4;
  border-color: #c6f0d5;
}

.connection-card.needs-reauth {
  background-color: #fef9e7;
  border-color: #f39c12;
  flex-direction: column;
  align-items: flex-start;
}

.badge-reauth {
  background-color: #f39c12;
  color: white;
}

.reauth-message {
  color: #7f8c8d;
  font-size: 0.9rem;
  margin: 0.5rem 0;
}

.connection-card.disconnected {
  background-color: #f8f9fa;
  border-color: #e0e0e0;
}

.connection-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 500;
}

.badge-connected {
  background-color: #27ae60;
  color: white;
}

.badge-disconnected {
  background-color: #bdc3c7;
  color: white;
}

.connected-date {
  color: #7f8c8d;
  font-size: 0.85rem;
}

.btn-disconnect {
  background-color: #e74c3c;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-disconnect:hover {
  background-color: #c0392b;
}

.btn-disconnect:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.error-alert {
  background-color: #fee2e2;
  color: #c0392b;
  padding: 0.75rem 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
  margin-top: 1rem;
}

/* Confirmation Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.confirm-modal {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.confirm-modal h3 {
  margin: 0 0 0.75rem;
  color: #2c3e50;
}

.confirm-modal p {
  color: #7f8c8d;
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 1.5rem;
}

.confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.btn-text {
  background: none;
  border: none;
  color: #7f8c8d;
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0.5rem 1rem;
}

.btn-text:hover {
  color: #2c3e50;
}

.btn-danger {
  background-color: #e74c3c;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
}

.btn-danger:hover {
  background-color: #c0392b;
}

.preferences-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.toggle-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  cursor: pointer;
  padding: 0.75rem;
  border-radius: 6px;
  border: 1px solid #eee;
  font-size: 0.95rem;
  color: #2c3e50;
}

.toggle-row:hover {
  background-color: #f8f9fa;
}

.toggle-row input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
  margin-top: 2px;
  flex-shrink: 0;
}

.toggle-row input[type="checkbox"]:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.toggle-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.toggle-label {
  font-weight: 500;
}

.toggle-hint {
  font-size: 0.8rem;
  color: #7f8c8d;
}

.btn-view-receipts {
  display: inline-block;
  padding: 0.5rem 1rem;
  background-color: #7f8c8d;
  color: white;
  border-radius: 4px;
  font-size: 0.9rem;
  text-decoration: none;
  transition: background-color 0.2s;
}

.btn-view-receipts:hover {
  background-color: #6c7a7d;
}

.btn-setup {
  display: inline-block;
  padding: 0.5rem 1rem;
  background-color: #3498db;
  color: white;
  border-radius: 4px;
  font-size: 0.9rem;
  text-decoration: none;
  transition: background-color 0.2s;
}

.btn-setup:hover {
  background-color: #2980b9;
}

.btn-manage {
  display: inline-block;
  padding: 0.5rem 1rem;
  background-color: #7f8c8d;
  color: white;
  border-radius: 4px;
  font-size: 0.9rem;
  text-decoration: none;
  transition: background-color 0.2s;
}

.btn-manage:hover {
  background-color: #6c7a7d;
}

/* Pending forwarding confirmation styles */
.connection-card.pending {
  border-color: #f39c12;
  background-color: #fef9e7;
}

.badge-pending {
  background-color: #f39c12;
  color: white;
}

.pending-instructions {
  margin: 1rem 0;
  padding: 0.75rem;
  background-color: white;
  border-radius: 4px;
  font-size: 0.9rem;
}

.pending-instructions p {
  margin: 0 0 0.5rem 0;
  color: #2c3e50;
  font-weight: 500;
}

.pending-instructions ol {
  margin: 0;
  padding-left: 1.25rem;
  color: #7f8c8d;
}

.pending-instructions li {
  margin: 0.25rem 0;
}

.btn-view-confirmation {
  padding: 0.5rem 1rem;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-view-confirmation:hover {
  background-color: #2980b9;
}

.btn-confirm-verified {
  padding: 0.5rem 1rem;
  background-color: #27ae60;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-confirm-verified:hover:not(:disabled) {
  background-color: #219a52;
}

.btn-confirm-verified:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Verification link box */
.verification-link-box {
  margin: 1rem 0;
  padding: 1rem;
  background: #e8f4fc;
  border-radius: 6px;
  text-align: center;
}

.verification-link {
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background: #3498db;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  font-weight: 500;
  transition: background-color 0.2s;
}

.verification-link:hover {
  background: #2980b9;
}

/* Pending action buttons */
.pending-action-buttons {
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  flex-wrap: wrap;
}

/* Waiting state */
.connection-card.waiting {
  border-color: #9b59b6;
  background-color: #f5eef8;
}

.badge-waiting {
  background-color: #9b59b6;
  color: white;
}

.waiting-indicator {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #7f8c8d;
  margin-top: 0.75rem;
}

.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid #eee;
  border-top-color: #9b59b6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Confirmation email modal */
.confirmation-email-modal {
  background: white;
  border-radius: 8px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.confirmation-email-modal .modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #eee;
}

.confirmation-email-modal .modal-header h3 {
  margin: 0;
  color: #2c3e50;
}

.confirmation-email-modal .btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #7f8c8d;
  cursor: pointer;
  line-height: 1;
}

.confirmation-email-modal .btn-close:hover {
  color: #2c3e50;
}

.confirmation-email-modal .modal-instructions {
  padding: 1rem 1.5rem;
  margin: 0;
  background-color: #e8f4fc;
  color: #2980b9;
  font-size: 0.9rem;
}


.confirmation-email-modal .email-loading {
  padding: 2rem;
  text-align: center;
  color: #7f8c8d;
}

/* Modal verification link */
.modal-verification-link {
  padding: 1rem 1.5rem;
  background: #d4edda;
  text-align: center;
}

.verification-link-btn {
  display: inline-block;
  padding: 0.75rem 2rem;
  background: #27ae60;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  font-weight: 500;
  font-size: 1rem;
  transition: background-color 0.2s;
}

.verification-link-btn:hover {
  background: #219a52;
}

/* Email content wrapper */
.email-content-wrapper {
  padding: 0 1.5rem 1rem;
  max-height: 300px;
  overflow-y: auto;
}

.email-content-label {
  font-size: 0.8rem;
  color: #7f8c8d;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
  padding-top: 1rem;
}

.confirmation-email-modal .email-content {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 4px;
  font-size: 0.9rem;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-x: auto;
}

/* Modal footer */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #eee;
  background: #f8f9fa;
  border-radius: 0 0 8px 8px;
}

.btn-secondary {
  padding: 0.5rem 1rem;
  background: #ecf0f1;
  color: #7f8c8d;
  border: none;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-secondary:hover {
  background: #bdc3c7;
  color: #2c3e50;
}

/* Household styles */
.household-card {
  background-color: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 1rem;
}

.household-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.household-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.household-name {
  font-weight: 600;
  color: #2c3e50;
}

.member-count {
  font-size: 0.85rem;
  color: #7f8c8d;
}

.household-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-invite {
  background-color: #27ae60;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-invite:hover {
  background-color: #219a52;
}

.btn-leave {
  background-color: #e74c3c;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-leave:hover {
  background-color: #c0392b;
}

.members-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.member-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: white;
  border-radius: 4px;
  border: 1px solid #eee;
}

.member-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.member-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
}

.member-avatar-placeholder {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #3498db;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9rem;
}

.member-details {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.member-name {
  color: #2c3e50;
}

.owner-badge {
  background-color: #3498db;
  color: white;
  padding: 0.15rem 0.5rem;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 500;
}

.member-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.sharing-badge {
  background-color: #d4edda;
  color: #155724;
  padding: 0.2rem 0.5rem;
  border-radius: 10px;
  font-size: 0.75rem;
}

.not-sharing-badge {
  background-color: #f8f9fa;
  color: #6c757d;
  padding: 0.2rem 0.5rem;
  border-radius: 10px;
  font-size: 0.75rem;
}

.btn-remove-member {
  background: none;
  border: none;
  color: #e74c3c;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0 0.25rem;
  line-height: 1;
}

.btn-remove-member:hover {
  color: #c0392b;
}

.invite-link-box {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
}

.invite-link-input {
  flex: 1;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.85rem;
  background-color: #f8f9fa;
}

.btn-copy {
  background-color: #3498db;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  white-space: nowrap;
}

.btn-copy:hover {
  background-color: #2980b9;
}

.btn-generate {
  background-color: #27ae60;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
}

.btn-generate:hover {
  background-color: #219a52;
}

.btn-generate:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.invite-actions {
  margin-top: 1rem;
  text-align: center;
}

/* Invite modal styles */
.invite-modal {
  max-width: 450px;
}

.invite-section {
  margin-bottom: 0.5rem;
}

.invite-section h4 {
  margin: 0 0 0.25rem 0;
  color: #2c3e50;
  font-size: 1rem;
}

.invite-section p {
  margin: 0 0 0.75rem 0;
  font-size: 0.85rem;
}

.section-divider {
  display: flex;
  align-items: center;
  margin: 1rem 0;
  color: #bdc3c7;
  font-size: 0.85rem;
}

.section-divider::before,
.section-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background-color: #e0e0e0;
}

.section-divider span {
  padding: 0 0.75rem;
}

.email-add-form {
  display: flex;
  gap: 0.5rem;
}

.email-input {
  flex: 1;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
}

.email-input:focus {
  outline: none;
  border-color: #3498db;
}

.btn-add-email {
  background-color: #27ae60;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  white-space: nowrap;
}

.btn-add-email:hover:not(:disabled) {
  background-color: #219a52;
}

.btn-add-email:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.success-message {
  background-color: #d4edda;
  color: #155724;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  font-size: 0.85rem;
  margin-bottom: 0.75rem;
}

/* Pending invites styles */
.pending-invites-section {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e0e0e0;
}

.pending-invites-section h3 {
  color: #2c3e50;
  font-size: 1rem;
  margin: 0 0 0.25rem 0;
}

.pending-invites-hint {
  color: #7f8c8d;
  font-size: 0.85rem;
  margin: 0 0 1rem 0;
}

.pending-invite-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #fff8e6;
  border: 1px solid #f0d78c;
  border-radius: 6px;
  margin-bottom: 0.5rem;
}

.pending-invite-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.pending-household-name {
  font-weight: 600;
  color: #2c3e50;
}

.pending-invite-from {
  font-size: 0.85rem;
  color: #7f8c8d;
}

.pending-invite-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-accept-invite {
  background-color: #27ae60;
  color: white;
  border: none;
  padding: 0.4rem 0.75rem;
  border-radius: 4px;
  font-size: 0.85rem;
  cursor: pointer;
}

.btn-accept-invite:hover:not(:disabled) {
  background-color: #219a52;
}

.btn-accept-invite:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-decline-invite {
  background: none;
  border: 1px solid #bdc3c7;
  color: #7f8c8d;
  padding: 0.4rem 0.75rem;
  border-radius: 4px;
  font-size: 0.85rem;
  cursor: pointer;
}

.btn-decline-invite:hover:not(:disabled) {
  border-color: #e74c3c;
  color: #e74c3c;
}

.btn-decline-invite:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Sent invites section */
.sent-invites-section {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;
}

.sent-invites-section h4 {
  margin: 0 0 0.75rem 0;
  font-size: 0.9rem;
  color: #7f8c8d;
  font-weight: 500;
}

.sent-invite-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: #fef9e7;
  border: 1px solid #f0d78c;
  border-radius: 4px;
  margin-bottom: 0.5rem;
}

.sent-invite-info {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.sent-invite-email {
  font-size: 0.9rem;
  color: #2c3e50;
}

.sent-invite-expires {
  font-size: 0.75rem;
  color: #7f8c8d;
}

.btn-revoke-invite {
  padding: 0.25rem 0.5rem;
  background: none;
  border: 1px solid #e74c3c;
  color: #e74c3c;
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: pointer;
}

.btn-revoke-invite:hover:not(:disabled) {
  background: #e74c3c;
  color: white;
}

.btn-revoke-invite:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
</style>
